import {
  CKChipListNext,
  CKLayoutHorizontal,
  CKMultiPickerNext,
  CKMultiPickerNextProps,
  CKSelectableItem,
} from 'clearkit';
import { FC } from 'react';

import { componentDisplayNames, Filter, FilterComposition } from './Filter';

type FilterMultiPickerComposition = Omit<FilterComposition, 'Input'>;

const {
  helpTextDisplayName,
  iconDisplayName,
  titleDisplayName,
} = componentDisplayNames;

type FilterMultiPickerProps = Omit<
  CKMultiPickerNextProps,
  'onChange' | 'value'
> & {
  onChange: (value: string[]) => void;
  value?: string[];
};

export const FilterMultiPicker: FC<FilterMultiPickerProps> &
  FilterMultiPickerComposition = ({
  children,
  className,
  items,
  onChange,
  placeholder = 'Choose filter or add custom value',
  value,
  ...rest
}) => {
  const handleOnChange = (value: CKSelectableItem[]) => {
    onChange(value.map((item) => item.value));
  };

  const handleClearFilter = () => {
    onChange([]);
  };

  const handleRemoveFilter = (item: CKSelectableItem) => {
    if (value) onChange(value.filter((valueItem) => valueItem !== item.value));
  };

  const lookupLabel = (value: string) =>
    items?.find((item) => item.value === value)?.label || value;

  return (
    <Filter
      className={className}
      filterCount={value?.length}
      onClearFilter={handleClearFilter}
    >
      {children}
      <Filter.Input className="space-y-2">
        <CKMultiPickerNext
          {...rest}
          autoFocus
          canAddCustomValue
          items={items}
          onChange={handleOnChange}
          placeholder={placeholder}
          value={value?.map((item) => ({
            label: lookupLabel(item),
            value: item,
          }))}
        />
        <CKLayoutHorizontal gap=".25rem" justifyContent="start">
          <CKChipListNext
            className="gap-1"
            items={value?.map((item) => ({
              label: lookupLabel(item),
              value: item,
            }))}
            removeItem={(newValue) =>
              handleRemoveFilter(newValue as CKSelectableItem)
            }
            shouldOverflow={false}
          />
        </CKLayoutHorizontal>
      </Filter.Input>
    </Filter>
  );
};

FilterMultiPicker.HelpText = Filter.HelpText;
FilterMultiPicker.HelpText.displayName = helpTextDisplayName;

FilterMultiPicker.Icon = Filter.Icon;
FilterMultiPicker.Icon.displayName = iconDisplayName;

FilterMultiPicker.Title = Filter.Title;
FilterMultiPicker.Title.displayName = titleDisplayName;
